import classNames from 'classnames';
import turfDistance from '@turf/distance';
import { pointItemToLngLat } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { TrailId } from 'types/Trails';
import { AlgoliaHit, AlgoliaResultType } from 'types/Search/Algolia';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { MapStatsUtil } from 'utils/map_stats_util';
import { SearchResultsUtil } from 'utils/search_results_util';
import { logTrailCardClicked, TrailDetailsReferralSources } from 'utils/trail_analytics_helpers';
import HighlightedResultName from '../HighlightedResultName/HighlightedResultName';
import SearchResultIcon from '../SearchResultIcon';
import * as styles from './styles/styles.module.scss';
import { XLATE_NONE } from '../../../Constants';

type InfoProps = {
  className: string;
  title: string;
};

function Info({ className, title }: InfoProps) {
  return (
    <span className={classNames(className, XLATE_NONE)} title={title}>
      {title}
    </span>
  );
}

type Props = {
  data: AlgoliaHit;
  displayMetric: boolean;
  handleItemClick: (url: string, item: AlgoliaHit, position: number) => void;
  hoverHandle: (index: number) => void;
  index: number;
  isSelected: boolean;
  latLngForDisplayDistance?: [number, number];
  term?: string;
};

const SearchResultItem = ({ term = '', data, index, isSelected, displayMetric, latLngForDisplayDistance, hoverHandle, handleItemClick }: Props) => {
  const languageRegionCode = useLanguageRegionCode();

  // We normalize the Algolia hit here, but we have to use the original non-normalized response
  // in the callback as callers expect some of that data.
  const { highlightedName, url } = SearchResultsUtil.formatSearchResult(data, languageRegionCode);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (data.type === AlgoliaResultType.Trail) {
      logTrailCardClicked(data.ID as TrailId, TrailDetailsReferralSources.Search);
    }

    handleItemClick(url, data, index);
  };

  let formattedName = data.name;
  if (latLngForDisplayDistance) {
    const lngLat = latLngForDisplayDistance.slice().reverse();
    const areaDistanceTo = turfDistance(pointItemToLngLat(data), lngLat) * 1000;
    const formattedDistance = MapStatsUtil.metersToFormattedUserUnits(areaDistanceTo, 2, displayMetric, languageRegionCode);
    formattedName = `${formattedName}  (${formattedDistance})`;
  }

  return (
    <div
      role="presentation"
      className={classNames('algolia-hit', { 'hit-selected': isSelected, [styles.selected]: isSelected })}
      onMouseEnter={() => hoverHandle(index)}
      onTouchStart={() => hoverHandle(index)}
      onClick={handleClick}
    >
      <a className={styles.hitLink} href={LanguageSupportUtil.wrapUrlSafe(url, languageRegionCode)}>
        <SearchResultIcon isActive={isSelected} isSmallVariant result={data} />
        <span className="search-text">
          <span className={classNames(styles.name, XLATE_NONE)} title={formattedName}>
            <HighlightedResultName highlightedName={highlightedName} />
          </span>
          <br />
          <Info className="trail-search-info" title={data.type === AlgoliaResultType.Trail ? SearchResultsUtil.formatTrailInfo(data) : ''} />
          <Info className="location-search-info" title={data.type !== AlgoliaResultType.Trail ? SearchResultsUtil.formatNonTrailInfo(data) : ''} />
        </span>
      </a>
    </div>
  );
};

export default SearchResultItem;
